<template>
  <section class="support-tasks ui-page">
    <ticket-filters @change="setFilters" />
    <AppErrorState v-if="error" :message="error" @retry="load" />
    <div class="ui-kanban" :aria-busy="loading">
      <section v-for="column in columns" :key="column.status" class="support-board__column ui-kanban-column">
        <header>
          <div><span class="support-board__dot" :class="`support-board__dot--${column.status}`"></span><h2>{{ column.label }}</h2></div>
          <strong>{{ column.count }}</strong>
        </header>
        <AppContentSkeleton v-if="loading && !column.items.length" layout="block" :label="`Loading ${column.label} tickets`" />
        <draggable
          v-show="!loading || column.items.length"
          :list="column.items"
          group="support-tickets"
          :disabled="!canEdit"
          class="support-board__list"
          ghost-class="support-board__ghost"
          :data-status="column.status"
          @add="onDrop($event, column.status)"
        >
          <ticket-card
            v-for="ticket in column.items"
            :key="ticket.id"
            :ticket="ticket"
            @open="openTicket"
          />
        </draggable>
        <app-no-results
          v-if="!loading && !error && !column.items.length"
          design="new"
          small
          :title="`No ${column.label.toLowerCase()} tickets`"
          text=""
        />
        <app-button
          v-if="column.hasMore"
          class="support-board__more"
          design="new"
          size="small"
          theme="dark"
          :disabled="column.loadingMore"
          @click.native="loadMore(column)"
        >
          {{ column.loadingMore ? 'Loading…' : 'Show more' }}
        </app-button>
      </section>
    </div>

    <modal name="support-waiting-reason" :width="460" height="auto" :adaptive="true" class="modal-not-overflow">
      <section class="support-waiting-dialog">
        <app-modal-header design="new" title="What are we waiting for?" @close="cancelWaiting" />
        <div class="support-waiting-dialog__body">
          <label v-for="option in waitingOptions" :key="option.value">
            <input v-model="waitingFor" type="radio" :value="option.value">
            <span><strong>{{ option.label }}</strong><small>{{ option.help }}</small></span>
          </label>
          <footer>
            <app-button design="new" theme="dark" @click.native="cancelWaiting">Cancel</app-button>
            <app-button design="new" theme="success" :disabled="!waitingFor || working" @click.native="confirmWaiting">Move to Waiting</app-button>
          </footer>
        </div>
      </section>
    </modal>
  </section>
</template>

<script>
import draggable from 'vuedraggable'
import AppContentSkeleton from '@/components/AppContentSkeleton.vue'
import AppErrorState from '@/components/AppErrorState.vue'
import { getSupportTickets, postSupportTicketTransition } from '@/http'
import { techSupportKey } from '@/utils/techSupportAccess'
import TicketCard from './components/TicketCard'
import TicketFilters from './components/TicketFilters'
import { apiError, idempotencyKey, normalizePage } from './ticketState'

export default {
  name: 'SupportTicketTasks',
  components: { draggable, AppContentSkeleton, AppErrorState, TicketCard, TicketFilters },
  data() {
    return {
      loading: true,
      working: false,
      error: '',
      filters: {},
      pending: null,
      waitingFor: 'requester',
      columns: [
        { status: 'new', label: 'New', items: [], count: 0, page: 1, hasMore: false, loadingMore: false },
        { status: 'in_progress', label: 'In progress', items: [], count: 0, page: 1, hasMore: false, loadingMore: false },
        { status: 'waiting', label: 'Waiting', items: [], count: 0, page: 1, hasMore: false, loadingMore: false }
      ],
      waitingOptions: [
        { value: 'requester', label: 'Requester', help: 'The employee needs to answer or add information.' },
        { value: 'internal_team', label: 'Internal team', help: 'Another colleague or developer needs to respond.' },
        { value: 'external_party', label: 'External party', help: 'A vendor or external system is blocking progress.' }
      ]
    }
  },
  created() {
    if (this.$route.query.focus === 'waiting') this.filters = { waiting_for: 'requester,internal_team,external_party' }
    this.load()
    this.$root.$on('support-ticket-refresh', this.load)
    this.$root.$on('support-ticket-created', this.onCreated)
  },
  beforeDestroy() {
    this.$root.$off('support-ticket-refresh', this.load)
    this.$root.$off('support-ticket-created', this.onCreated)
  },
  computed: {
    canEdit() { return this.$store.getters['permissions/can'](techSupportKey('support', 'edit')) }
  },
  methods: {
    load(showLoading = true) {
      if (showLoading) this.loading = true
      this.error = ''
      return Promise.all(this.columns.map(column => this.loadColumnPages(column))).then(columnResponses => {
        columnResponses.forEach((responses, index) => {
          const items = responses.reduce((result, response) => [...result, ...normalizePage(response.data)], [])
          const firstPage = responses[0].data
          const lastPage = responses[responses.length - 1].data
          this.columns[index].items = this.columns[index].status === 'new' ? this.sortNew(items) : items
          this.columns[index].count = firstPage.count ?? items.length
          this.columns[index].page = Math.min(
            this.columns[index].page,
            Math.max(1, Math.ceil(this.columns[index].count / 100))
          )
          this.columns[index].hasMore = Boolean(lastPage.next)
        })
      }).catch(error => { this.error = apiError(error, 'Unable to load the support board.') })
        .finally(() => { this.loading = false })
    },
    loadColumnPages(column) {
      const requests = []
      for (let page = 1; page <= column.page; page += 1) {
        requests.push(getSupportTickets({
          ...this.filters,
          status: column.status,
          page_size: 100,
          page
        }))
      }
      return Promise.all(requests)
    },
    setFilters(filters) {
      this.filters = filters
      this.columns.forEach(column => { column.page = 1 })
      this.load()
    },
    loadMore(column) {
      if (!column.hasMore || column.loadingMore) return Promise.resolve()
      column.loadingMore = true
      this.error = ''
      return getSupportTickets({
        ...this.filters,
        status: column.status,
        page_size: 100,
        page: column.page + 1
      }).then(response => {
        const knownIds = new Set(column.items.map(item => item.id))
        const items = normalizePage(response.data).filter(item => !knownIds.has(item.id))
        column.items = [...column.items, ...items]
        if (column.status === 'new') column.items = this.sortNew(column.items)
        column.count = response.data.count ?? column.items.length
        column.page += 1
        column.hasMore = Boolean(response.data.next)
      }).catch(error => { this.error = apiError(error, 'Unable to load more support tickets.') })
        .finally(() => { column.loadingMore = false })
    },
    onCreated(ticket) {
      if (ticket.status !== 'new') return
      this.columns[0].items = this.sortNew([...this.columns[0].items, ticket])
      this.columns[0].count += 1
    },
    sortNew(items) {
      return items.slice().sort((first, second) => {
        return new Date(first.status_changed_at).getTime() - new Date(second.status_changed_at).getTime() || first.id - second.id
      })
    },
    openTicket(ticket) {
      this.$router.push({ name: this.$route.name, query: { ...this.$route.query, ticket: ticket.id } }).catch(() => {})
    },
    onDrop(event, status) {
      const ticket = event.item?._underlying_vm_ || this.columns.find(column => column.status === status)?.items[event.newIndex]
      if (ticket) this.requestTransition({ ticket, status }, true)
    },
    requestTransition(payload, fromDrag = false) {
      if (payload.status === payload.ticket.status) { if (fromDrag) this.load(false); return }
      if (payload.status === 'waiting') {
        this.pending = payload
        this.waitingFor = 'requester'
        if (fromDrag) this.load(false)
        this.$modal.show('support-waiting-reason')
        return
      }
      this.performTransition(payload.ticket, payload.status)
    },
    performTransition(ticket, status, waitingFor = null) {
      if (!this.canEdit) return Promise.resolve()
      this.working = true
      return postSupportTicketTransition(ticket.id, {
        status,
        waiting_for: waitingFor,
        expected_state_version: ticket.state_version
      }, idempotencyKey()).then(() => this.load(false)).catch(error => {
        this.error = apiError(error)
        return this.load(false)
      }).finally(() => { this.working = false })
    },
    confirmWaiting() {
      if (!this.pending || !this.waitingFor) return
      const pending = this.pending
      this.$modal.hide('support-waiting-reason')
      this.pending = null
      this.performTransition(pending.ticket, 'waiting', this.waitingFor)
    },
    cancelWaiting() { this.pending = null; this.$modal.hide('support-waiting-reason') }
  }
}
</script>

<style lang="scss" scoped>
.support-board__column > header { display: flex; align-items: center; justify-content: space-between; min-height: 40px; margin-bottom: $ui-space-12; padding: 0 $ui-space-4; }
.support-board__column > header div { display: flex; align-items: center; gap: $ui-space-8; }
.support-board__column h2 { margin: 0; color: $ui-ink; font: 600 $ui-font-size-section-title $ui-font-heading; }
.support-board__column header strong { display: inline-flex; min-width: 28px; height: 28px; align-items: center; justify-content: center; border-radius: 14px; background: $ui-surface; color: $ui-muted; font-size: $ui-font-size-metadata; }
.support-board__dot { width: 10px; height: 10px; border-radius: 50%; background: $ui-blue; }
.support-board__dot--in_progress { background: $ui-green; }
.support-board__dot--waiting { background: $ui-amber; }
.support-board__list { display: grid; min-height: 100px; gap: $ui-space-12; }
.support-board__ghost { opacity: .35; }
.support-board__more { width: 100%; margin-top: $ui-space-12; }
.support-tasks__error { display: flex; justify-content: space-between; margin-bottom: $ui-space-12; padding: $ui-space-12; border-radius: $ui-control-radius; background: rgba($ui-red, .08); color: $ui-red; }
.support-tasks__error button { border: 0; background: transparent; color: inherit; cursor: pointer; font-weight: 700; }
.support-waiting-dialog { background: $ui-page; }
.support-waiting-dialog__body { display: grid; gap: $ui-space-12; padding: $ui-space-20; }
.support-waiting-dialog label { display: flex; gap: $ui-space-12; padding: $ui-space-12; border: 1px solid $ui-line; border-radius: $ui-panel-radius; background: $ui-surface; cursor: pointer; }
.support-waiting-dialog label span { display: grid; gap: $ui-space-4; }
.support-waiting-dialog small { color: $ui-muted; font-size: $ui-font-size-metadata; }
.support-waiting-dialog footer { display: flex; justify-content: flex-end; gap: $ui-space-12; margin-top: $ui-space-8; }
</style>
